import { supabase, Profile } from '../lib/supabase';
import { UserProfile } from '../types';

// Converte do formato do banco para o formato da aplicação
const dbToApp = (profile: Profile): UserProfile => ({
    id: profile.id,
    full_name: profile.full_name || '',
    company_name: profile.company_name || '',
    prices: {
        gesso_simples: profile.price_gesso_simples || 0,
        forro_reforcado: profile.price_forro_reforcado || 0,
        parede_bloco: profile.price_bloco || 0,
        moldura: profile.price_moldura || 0,
        dilatacao: profile.price_dilatacao || 0,
        reboco: profile.price_reboco || 0,
        revestimento: profile.price_revestimento || 0,
        reboco_separado: profile.price_reboco_separado || 0,
        parede_bloco_separado: profile.price_parede_bloco_separado || 0,
        revestimento_separado: profile.price_revestimento_separado || 0,
    },
    custom_services: profile.custom_services || [],
});

// Converte do formato da aplicação para o formato do banco
const appToDb = (profile: Partial<UserProfile>): Partial<Profile> => {
    const dbProfile: Partial<Profile> = {};

    if (profile.full_name !== undefined) dbProfile.full_name = profile.full_name;
    if (profile.company_name !== undefined) dbProfile.company_name = profile.company_name;

    if (profile.prices) {
        dbProfile.price_gesso_simples = profile.prices.gesso_simples;
        dbProfile.price_forro_reforcado = profile.prices.forro_reforcado;
        dbProfile.price_bloco = profile.prices.parede_bloco;
        dbProfile.price_moldura = profile.prices.moldura;
        dbProfile.price_dilatacao = profile.prices.dilatacao;
        dbProfile.price_reboco = profile.prices.reboco;
        dbProfile.price_revestimento = profile.prices.revestimento;
        dbProfile.price_reboco_separado = profile.prices.reboco_separado;
        dbProfile.price_parede_bloco_separado = profile.prices.parede_bloco_separado;
        dbProfile.price_revestimento_separado = profile.prices.revestimento_separado;
    }

    if (profile.custom_services !== undefined) {
        dbProfile.custom_services = profile.custom_services;
    }

    return dbProfile;
};

export const profileService = {
    // Buscar perfil do usuário
    async getProfile(userId: string): Promise<UserProfile | null> {
        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();

        if (error) {
            console.error('Erro ao buscar perfil:', error);
            return null;
        }

        return data ? dbToApp(data as Profile) : null;
    },

    // Criar perfil inicial (chamado após signup)
    async createProfile(userId: string, profile: Partial<UserProfile>): Promise<UserProfile | null> {
        const dbData = {
            id: userId,
            ...appToDb(profile),
        };

        const { data, error } = await supabase
            .from('profiles')
            .insert(dbData)
            .select()
            .single();

        if (error) {
            console.error('Erro ao criar perfil:', error);
            return null;
        }

        return data ? dbToApp(data as Profile) : null;
    },

    // Atualizar perfil
    async updateProfile(userId: string, profile: Partial<UserProfile>): Promise<UserProfile | null> {
        const dbData = {
            ...appToDb(profile),
            updated_at: new Date().toISOString(),
        };

        const { data, error } = await supabase
            .from('profiles')
            .update(dbData)
            .eq('id', userId)
            .select()
            .single();

        if (error) {
            console.error('Erro ao atualizar perfil:', error);
            return null;
        }

        return data ? dbToApp(data as Profile) : null;
    },

    // Upsert perfil (cria se não existe, atualiza se existe)
    async upsertProfile(userId: string, profile: Partial<UserProfile>): Promise<UserProfile | null> {
        const existing = await this.getProfile(userId);

        if (existing) {
            return this.updateProfile(userId, profile);
        } else {
            return this.createProfile(userId, profile);
        }
    },
};
